import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import CameraCaptureInput from '@/features/scanner/CameraCaptureInput';
import { useScanSession } from '@/features/scanner/ScanSessionContext';
import { ImageDecodeError, prepareBillImage } from '@/services/image/prepareImage';

export default function ScannerPage() {
  const navigate = useNavigate();
  const { setPrepared } = useScanSession();
  const [error, setError] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);

  async function handleFile(file: File) {
    setError(null);
    setPreparing(true);
    try {
      const prepared = await prepareBillImage(file);
      setPrepared(prepared);
      navigate('/scan/preview');
    } catch (err) {
      setError(err instanceof ImageDecodeError ? err.message : 'Something went wrong reading that photo.');
    } finally {
      setPreparing(false);
    }
  }

  return (
    <div className="flex flex-col gap-6 p-4 pt-6">
      <header>
        <h1 className="text-xl font-bold text-slate-900">Scan Bill</h1>
        <p className="mt-1 text-sm text-slate-600">
          Position the entire bill inside the frame, with good lighting and no glare.
        </p>
      </header>

      <div className="flex aspect-[3/4] items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 bg-white text-center text-sm text-slate-400">
        <span className="px-8">The whole bill should be visible, flat, and well lit.</span>
      </div>

      {error && (
        <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {error}
        </p>
      )}

      <div className="flex flex-col gap-3">
        <CameraCaptureInput
          onCapture={(file) => void handleFile(file)}
          className="rounded-2xl bg-blue-700 px-6 py-5 text-lg font-semibold text-white shadow-sm active:bg-blue-800 disabled:opacity-60"
        >
          {preparing ? 'Preparing photo…' : 'Take Photo'}
        </CameraCaptureInput>

        <CameraCaptureInput
          galleryMode
          onCapture={(file) => void handleFile(file)}
          className="rounded-2xl border border-slate-300 bg-white px-6 py-4 text-base font-medium text-slate-700 active:bg-slate-100"
        >
          Use Existing Photo
        </CameraCaptureInput>
      </div>
    </div>
  );
}
